/**
 * 🧵 **THE VEFSTOFA SPINS — wool in, thread out. D127, 2026-09-28.**
 *
 * 🎯 **haugur, having heard the real process** (split tog from þel, comb, spin
 * on a halasnælda, ply): *"this process is to complicated for me … should we
 * make a Menu (house) where you work with textils"*. 🔴 **So the process is not
 * thrown away, it is moved INTO the house** — the house combs and spins over
 * time, the way the búr's fire cooks.
 *
 * | | His numbers |
 * |---|---|
 * | 🧶 → 🧵 | **1 `ull` → 4 `thradur`** |
 * | ⏱️ | **one thread every REAL MINUTE** — 1.2 game hours |
 * | ⛽ | **no fuel** — spinning needs hands, not heat |
 * | 🌙 | **it keeps spinning while you are away** |
 *
 * 📏 **The minute is a real rate**: a skilled spinner makes 30–50 m of yarn an
 * hour, and one real minute is 72 game minutes — about 50 m of yarn, about 25 m
 * of two-ply thread, roughly one fishing line.
 *
 * ## 🔴 How "while you are away" works, and it is the búr's way
 *
 * **Nothing ticks.** The row carries {@link SpinState}; whenever somebody opens
 * the house, moves an item or the screen polls, {@link resolveSpin} works out
 * how much was spun since and writes it back — `resolveHearth`'s catch-up,
 * without the fire. ⚠️ **The clock is the GAME's** (`gameHoursIn`), which stops
 * when nobody is in the world, exactly as a búr's cooking does.
 */

/** 🧶 The one thing that goes IN. */
export const SPIN_INPUT = 'ull';
/** 🧵 The one thing that comes OUT. */
export const SPIN_OUTPUT = 'thradur';
/** 🧵 **How many threads one wool makes.** 🎯 His number. */
export const THREADS_PER_WOOL = 4;
/** ⏱️ **Game hours per thread** — one real minute at 72×. 🎯 His number. */
export const SPIN_GAME_HOURS = 1.2;

/** 🧶 Slot 0 of a vefstofa takes wool; 🧵 slot 1 holds the thread. */
export const SPIN_IN_SLOT = 0;
export const SPIN_OUT_SLOT = 1;

/** The clock part of a vefstofa's row. */
export interface SpinState {
  /**
   * When the thread now being spun was started, in GAME hours — or `null` when
   * the house is idle.
   */
  readonly fromGameHours: number | null;
  /** 🧶 Threads still owed by a wool ALREADY taken off the stack, 0–3. */
  readonly owed: number;
}

export const IDLE_SPIN: SpinState = { fromGameHours: null, owed: 0 };

/** A slot as the row holds it. Only the two numbers this needs. */
interface Slot {
  readonly slot: number;
  readonly itemKey: string;
  readonly count: number;
}

/** 🧶🧵 May this item go into this slot of a vefstofa? */
export function spinSlotAccepts(slot: number, itemKey: string): boolean {
  if (slot === SPIN_IN_SLOT) return itemKey === SPIN_INPUT;
  if (slot === SPIN_OUT_SLOT) return itemKey === SPIN_OUTPUT;
  return false;
}

function countIn<S extends Slot>(stacks: readonly S[], slot: number): number {
  return stacks.find((s) => s.slot === slot)?.count ?? 0;
}

/**
 * 🧵 **Catch up on the spinning.**
 *
 * One thread per {@link SPIN_GAME_HOURS}, each wool taken off the stack when its
 * FIRST thread is done and good for {@link THREADS_PER_WOOL}. ⚠️ **It pauses
 * when the thread slot is full** and picks up when there is room again — the
 * búr's rule for a full output shelf. **Part of a thread is kept**: the clock
 * moves forward by whole threads, never to "now".
 *
 * @param maxThread how many threads fit in one slot
 * @returns the new stacks and clock, and whether anything changed (the caller
 *   writes back only then)
 */
export function resolveSpin<S extends Slot>(
  stacks: readonly S[],
  state: SpinState,
  nowGameHours: number,
  maxThread: number,
  make: (slot: number, itemKey: string, count: number) => S,
): { stacks: S[]; state: SpinState; changed: boolean } {
  let wool = countIn(stacks, SPIN_IN_SLOT);
  let thread = countIn(stacks, SPIN_OUT_SLOT);
  let owed = state.owed;
  let from = state.fromGameHours;
  const woolBefore = wool;
  const threadBefore = thread;

  /* ▶️ Start when there is something to spin. ⚠️ A FULL thread slot is not
     checked here: the idle rule below stops the clock again in the same call,
     and a second copy of that check could never matter — found by removing it. */
  if (from === null && (wool > 0 || owed > 0)) from = nowGameHours;

  /* 🔴 **A hair of tolerance, found by a test.** `from` moves forward by 1.2 at
     a time, and ten of those in floating point fall just short of 12 — so ten
     minutes away gave NINE threads. */
  while (from !== null && nowGameHours - from >= SPIN_GAME_HOURS - 1e-9) {
    if (owed === 0) {
      if (wool === 0) break;
      wool -= 1;
      owed = THREADS_PER_WOOL;
    }
    thread += 1;
    owed -= 1;
    from += SPIN_GAME_HOURS;
    if (thread >= maxThread) break;
  }

  /* ⏸️ Idle when there is nothing left, or nowhere to put the next thread. */
  if ((owed === 0 && wool === 0) || thread >= maxThread) from = null;

  const next: SpinState = { fromGameHours: from, owed };
  const changed =
    wool !== woolBefore ||
    thread !== threadBefore ||
    from !== state.fromGameHours ||
    owed !== state.owed;
  if (!changed) return { stacks: [...stacks], state, changed: false };

  const others = stacks.filter((s) => s.slot !== SPIN_IN_SLOT && s.slot !== SPIN_OUT_SLOT);
  const out: S[] = [...others];
  if (wool > 0) out.push(make(SPIN_IN_SLOT, SPIN_INPUT, wool));
  if (thread > 0) out.push(make(SPIN_OUT_SLOT, SPIN_OUTPUT, thread));
  return { stacks: out, state: next, changed: true };
}

/** 🧵 Is the house spinning right now? For the screen's sentence. */
export function isSpinning(state: SpinState): boolean {
  return state.fromGameHours !== null;
}
